import React from "react";
import { Line, LineChart, ReferenceArea, ReferenceDot, ReferenceLine, ResponsiveContainer, YAxis } from "recharts";
import { Card, CardContent } from "../ui/card";
import { BUILD, PRICES, REVEAL_IDX, STOCK, fmtPct, pathAt } from "./data";

export const Overline = ({ children, className = "" }) => (
  <div className={`text-[10px] uppercase tracking-[0.2em] text-muted-foreground ${className}`}>{children}</div>
);

export const Mini = ({ k, v, accent, testid }) => (
  <div data-testid={testid}>
    <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">{k}</div>
    <div className={`font-mono text-sm ${accent || ""}`}>{v}</div>
  </div>
);

export const MiniTable = ({ cols, rows, testid }) => (
  <table className="w-full text-[11px] font-mono" data-testid={testid}>
    <thead className="text-muted-foreground border-b border-border/60">
      <tr>{cols.map((c) => <th key={c} className="py-1 px-1.5 text-left font-medium">{c}</th>)}</tr>
    </thead>
    <tbody>
      {rows.map((r, i) => (
        <tr key={i} className="border-b border-border/30">
          {r.map((c, j) => <td key={j} className="py-1 px-1.5">{c}</td>)}
        </tr>
      ))}
    </tbody>
  </table>
);

export const ScoreDial = ({ score, size = "w-24 h-24" }) => (
  <div className={`relative ${size} shrink-0`} data-testid="kfm-score-dial">
    <svg viewBox="0 0 100 100" className="w-full h-full -rotate-90">
      <circle cx="50" cy="50" r="42" fill="none" stroke="hsl(0 0% 15%)" strokeWidth="9" />
      <circle cx="50" cy="50" r="42" fill="none" stroke={score >= 65 ? "#34d399" : score >= 40 ? "#fbbf24" : "#f87171"}
        strokeWidth="9" strokeLinecap="round" strokeDasharray={`${score * 2.64} 264`} />
    </svg>
    <div className="absolute inset-0 flex flex-col items-center justify-center">
      <span className="text-2xl font-black font-mono">{score}</span>
      <span className="text-[8px] uppercase tracking-[0.2em] text-muted-foreground">score</span>
    </div>
  </div>
);

export const StockHeader = ({ compact }) => (
  <Card className="border-emerald-500/30" data-testid="kfm-verdict-card">
    <CardContent className={compact ? "py-3" : "pt-5 pb-5"}>
      <div className="flex flex-wrap items-center gap-5">
        <ScoreDial score={STOCK.score} size={compact ? "w-16 h-16" : "w-24 h-24"} />
        <div className="flex-1 min-w-[280px]">
          <div className="flex items-center gap-3 flex-wrap mb-1">
            <span className={`${compact ? "text-2xl" : "text-3xl"} font-black tracking-tight`}>{STOCK.sym}</span>
            <span className="text-sm text-muted-foreground">
              {STOCK.name} · {STOCK.sector} · {STOCK.mcap} · Coverage {STOCK.coverage}
            </span>
          </div>
          <div className="text-xl font-black tracking-tight text-emerald-400" data-testid="kfm-verdict">{STOCK.verdict}</div>
          {!compact && (
            <p className="text-sm text-foreground/90 mt-1 max-w-3xl">
              Pros' thesis confirming (+4.1% vs sector since their estimated entry) · fresh 9-fund cohort, V3 band S8 ·
              zero skilled exits · earnings setup GREEN in 12 days · regime Compounding Drift.
            </p>
          )}
        </div>
      </div>
    </CardContent>
  </Card>
);

const Tile = ({ label, value, sub, chip, chipCls, testid }) => (
  <div className="rounded-md border border-border/70 bg-muted/20 px-4 py-3" data-testid={testid}>
    <div className="flex items-center justify-between gap-2">
      <Overline>{label}</Overline>
      {chip && <span className={`px-1.5 py-0.5 rounded border text-[9px] font-mono ${chipCls}`}>{chip}</span>}
    </div>
    <div className="font-mono text-2xl font-bold mt-1">{value}</div>
    <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>
  </div>
);

export const ThesisStrip = () => {
  const typ = pathAt(STOCK.day);
  const ahead = STOCK.sinceDisclosure >= typ.p50;
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="kfm-thesis-strip">
      <Tile testid="kfm-thesis-since-entry" label="Thesis confirmation · since pros' entry" value={`${fmtPct(STOCK.bpRel)} vs sector`}
        sub={`price $${STOCK.price.toFixed(2)} vs est. basis $${STOCK.basis.toFixed(2)} · BP ${STOCK.bpPct}th pct · validated factor`}
        chip="CONFIRMING" chipCls="bg-emerald-500/15 text-emerald-400 border-emerald-500/40" />
      <Tile testid="kfm-thesis-buyers-profit" label="Cohort buyers in profit" value="9 / 9"
        sub="all 9 new buyers above their est. cost · 9 / 11 incl. older skilled holders" />
      <Tile testid="kfm-thesis-since-disclosure" label="Since public disclosure" value={`${fmtPct(STOCK.sinceDisclosure)} vs sector`}
        sub={`day ${STOCK.day}/126 · typical S8 path ${fmtPct(typ.p50)} at this day · descriptive`}
        chip={ahead ? "AHEAD OF TYPICAL" : "BELOW TYPICAL"} chipCls="bg-sky-500/15 text-sky-300 border-sky-500/40" />
    </div>
  );
};

export const BasisSpark = ({ height = 56, labels = false }) => (
  <div style={{ height }} data-testid="kfm-basis-sparkline">
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={PRICES} margin={{ top: labels ? 16 : 4, right: labels ? 110 : 6, bottom: 4, left: labels ? 4 : 0 }}>
        <YAxis domain={[94, 114]} hide />
        <ReferenceArea x1={BUILD.x1} x2={BUILD.x2} fill="#34d399" fillOpacity={0.08}
          label={labels ? { value: "COHORT BUILD QUARTER", position: "insideTopLeft", fontSize: 10, fill: "#34d399" } : undefined} />
        <ReferenceLine y={STOCK.basis} stroke="#34d399" strokeDasharray="5 4"
          label={labels ? { value: `EST. BASIS $${STOCK.basis}`, position: "right", fontSize: 10, fill: "#34d399" } : undefined} />
        <ReferenceDot x={REVEAL_IDX} y={PRICES[REVEAL_IDX].px} r={labels ? 5 : 3} fill="#38bdf8" stroke="none"
          label={labels ? { value: "13F disclosed", position: "top", fontSize: 10, fill: "#38bdf8" } : undefined} />
        <ReferenceDot x={PRICES.length - 1} y={STOCK.price} r={labels ? 5 : 3} fill="#fff" stroke="none"
          label={labels ? { value: `PRICE $${STOCK.price}`, position: "right", fontSize: 11, fill: "#fff" } : undefined} />
        <Line type="monotone" dataKey="px" stroke="hsl(220 70% 60%)" strokeWidth={labels ? 2 : 1.5} dot={false} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  </div>
);

export const Legend = ({ items }) => (
  <div className="flex flex-wrap gap-4 text-[11px] text-muted-foreground">
    {items.map(([c, l]) => (
      <span key={l} className="flex items-center gap-1.5"><span className="w-3 h-1.5 rounded-sm" style={{ background: c }} />{l}</span>
    ))}
  </div>
);
